import React, {ReactNode, useId} from 'react';
import {alpha, Box, Paper, Typography} from '@mui/material';

interface SettingsSectionProps {
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
  // Rendered under the description, e.g. a section-level save button.
  actions?: ReactNode;
  // Tints the section to mark it as visible to SecurSpace admins only.
  admin?: boolean;
}

// One settings row: title and description on the left, fields on the right. The two halves wrap into a single
// column once the row is too narrow to hold both side by side.
const SettingsSection = ({title, description, icon, children, actions, admin = false}: SettingsSectionProps) => {
  const titleId = useId();

  return (
    <Paper
      component={'section'}
      aria-labelledby={titleId}
      variant={'outlined'}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1.71rem 3.43rem',
        p: {xs: '1.43rem', sm: '2rem'},
        borderRadius: '0.57rem',
        ...(admin && {
          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.05),
          borderColor: (theme) => alpha(theme.palette.secondary.main, 0.3),
        }),
      }}
    >
      <Box sx={{flex: '1 1 16rem', maxWidth: {md: '20rem'}, display: 'flex', flexDirection: 'column', gap: '0.71rem'}}>
        <Box
          aria-hidden
          sx={{
            width: '2.29rem',
            height: '2.29rem',
            borderRadius: '0.57rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => admin ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.1),
            color: admin ? 'common.white' : 'secondary.main',
            '& svg': {fontSize: '1.29rem'},
          }}
        >
          {icon}
        </Box>
        <Typography id={titleId} variant={'h6'} component={'h2'} color={'textPrimary'}>{title}</Typography>
        <Typography variant={'body2'} color={'textSecondary'}>{description}</Typography>
        {actions && <Box sx={{mt: '0.43rem'}}>{actions}</Box>}
      </Box>
      <Box sx={{flex: '999 1 32rem', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.57rem'}}>
        {children}
      </Box>
    </Paper>
  );
};

// Two equal columns on wider screens, one on phones.
export const SettingsFieldGrid = ({children}: { children: ReactNode }) => (
  <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))'}, gap: '1.43rem 2.29rem', alignItems: 'start'}}>
    {children}
  </Box>
);

export default SettingsSection;
